import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AuthService } from '@core/services/auth.service';
import { HttpLoadingService } from '@core/services/http-loading.service';
import { GeneralLayout } from './layouts/general/general';
import { AuthLayout } from './layouts/auth/auth';
import { ToastComponent } from '@shared/toast/toast.component';

/**
 * The screens the auth layout owns. Every one lives under `src/app/authenticate/`, and none of
 * them may be wrapped in the admin chrome — including `force-reset-password`, which a first-login
 * user reaches while already holding a token.
 *
 * `/shared-cog-reports` is the public, password-protected COG Report link (epic 87.8): its viewer
 * may have no account at all, so it never gets the admin chrome either.
 */
const AUTH_ROUTES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/force-reset-password',
  '/shared-cog-reports',
];

function isAuthRoute(url: string): boolean {
  return AUTH_ROUTES.some((route) => url.startsWith(route));
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [GeneralLayout, AuthLayout, ToastComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly authService = inject(AuthService);
  private readonly httpLoading = inject(HttpLoadingService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly isLoggedIn = this.authService.isLoggedIn;
  /** Global overlay while any HttpClient request is in flight (see {@link HttpLoadingInterceptor}). */
  readonly httpLoadingActive = this.httpLoading.isLoading;

  /** Tracked the same way the general layout tracks its own route-dependent chrome. */
  private readonly onAuthRoute = signal(isAuthRoute(this.router.url));

  /**
   * Which layout is on screen.
   *
   * Not `isLoggedIn` alone. Signing in sets the access token before the navigation away from
   * `/login` has finished — it has a lazy chunk to fetch first — and on that token alone the root
   * swapped to the admin layout while the router was still showing the login form. The result was
   * the login page rendered inside the sidebar for as long as the navigation took, which is why
   * it appeared only sometimes: with the dashboard chunk already cached, the gap is invisible.
   *
   * Waiting for the route as well as the token closes that window, and keeps every other auth
   * screen out of the admin chrome for the same reason.
   */
  readonly showGeneralLayout = computed(() => this.isLoggedIn() && !this.onAuthRoute());

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.onAuthRoute.set(isAuthRoute(event.urlAfterRedirects)));
  }
}
